// MCP catalog -> OpenAI tools array reshape.
//
// The cocoon-client polyfill (when its UPSTREAM_NATIVE_TOOLS toggle is off,
// the default) renders this OpenAI-shape array into the Qwen3 Hermes-style
// <tools>...</tools> block server-side, one JSON object per line. The shape
// is faithful pass-through: name, description, and parameters (= inputSchema)
// are preserved unchanged so `additionalProperties: false` from the upstream
// schema enforcement reaches the model.
//
// This module ALSO centralizes the SDK-boundary type casts that orchestrator.ts
// uses to read/write tool fields the SDK does not yet type publicly. Each cast
// is a named function with a documented local interface, instead of scattering
// `as unknown as` sites across orchestrator.ts.

import type { ToolDef, OpenaiTool, OpenaiToolCall, ToolMessage } from './types.js';
import type { Chunk, InferenceRequest } from '@alphatoncapital/shroud-sdk';

/** Reshape MCP-catalog ToolDef[] into the OpenAI tools array shape. */
export function toOpenaiTools(defs: ToolDef[]): OpenaiTool[] {
  return defs.map((d) => ({
    type: 'function',
    function: {
      name: d.name,
      description: d.description,
      parameters:
        d.inputSchema ?? { type: 'object', properties: {}, additionalProperties: false },
    },
  }));
}

// === SDK-boundary cast adapters ===
//
// The SDK's public InferenceRequest does not include `tools`, `tool_choice`,
// or `chat_template_kwargs` — those fields live on the wire end-to-end, but
// the SDK keeps its public surface inference-only for now. The cocoon-client
// polyfill consumes these fields server-side. The local interfaces below
// document the actual wire shape; the cast helpers convert between SDK-typed
// and wire-typed views in one audited place.

/** Wire-shape view of InferenceRequest body that includes the tool fields. */
interface OpenaiInferenceBody {
  model: string;
  messages: unknown[];
  stream?: boolean;
  tools?: OpenaiTool[];
  tool_choice?:
    | 'auto'
    | 'none'
    | 'required'
    | { type: 'function'; function: { name: string } };
  chat_template_kwargs?: { enable_thinking?: boolean; [k: string]: unknown };
}

/** Wire-shape view of a streamed Chunk that may carry tool_calls. */
interface OpenaiToolCallChunk {
  content: string;
  reasoningContent: string;
  toolCalls?: unknown[];
}

/**
 * Wave-A assistant-turn shape that includes the round-0 tool_calls field.
 * The cocoon-client polyfill rewrites this back to inline `<tool_call>` so
 * the model sees its own previous turn in the shape it was trained on.
 */
interface OpenaiAssistantToolCallsMessage {
  role: 'assistant';
  content: string;
  tool_calls: OpenaiToolCall[];
}

/** Cast a wire inference body to the SDK-typed InferenceRequest the SDK expects. */
export function asInferenceRequest(body: OpenaiInferenceBody): InferenceRequest {
  return body as unknown as InferenceRequest;
}

/** Cast a streamed SDK Chunk to the view that exposes toolCalls. */
export function asOpenaiChunk(chunk: Chunk): OpenaiToolCallChunk {
  return chunk as unknown as OpenaiToolCallChunk;
}

/**
 * Build a tool-result message; the cast prevents the SDK's typed Message
 * from rejecting the role:'tool' shape with tool_call_id.
 */
export function asOpenaiToolMessage(call_id: string, content: string): ToolMessage {
  return { role: 'tool', tool_call_id: call_id, content };
}

/**
 * Build an assistant-turn-with-tool_calls message. The polyfill rewrites
 * this to inline `<tool_call>` server-side so the model sees its own
 * previous turn in canonical Qwen3 shape.
 */
export function asAssistantToolCallsMessage(
  content: string,
  tool_calls: OpenaiToolCall[],
): OpenaiAssistantToolCallsMessage {
  return { role: 'assistant', content, tool_calls };
}
